import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Menu, MenuItem, Slider, Stack, Tooltip, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import ClosedCaptionRoundedIcon from "@mui/icons-material/ClosedCaptionRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SubtitlesRoundedIcon from "@mui/icons-material/SubtitlesRounded";
import { getActiveCues, parseVttCues } from "helpers/videoCaptions";

const BAR_BG = "rgba(24, 23, 44, 0.96)";
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const formatPlayerTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (value) => String(value).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${pad(minutes)}:${pad(secs)}`;
};

const controlSx = {
  color: "#fff",
  width: 34,
  height: 34,
  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
};

const pillSx = {
  color: "#fff",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  minWidth: 30,
  height: 26,
  px: 0.75,
  borderRadius: "6px",
  textTransform: "uppercase",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
};

const pillActiveSx = {
  ...pillSx,
  backgroundColor: "rgba(255,255,255,0.18)",
};

/**
 * Synthesia-style player chrome.
 *
 * The native controls are hidden and replaced with a dark bar so chapters,
 * language and captions can be offered together. The <video> element itself is
 * still native, so pause/seek timing stays exact.
 */
const VideoPlayer = ({
  src,
  title,
  captionsText,
  languageCode,
  languages = [],
  chapters = [],
  resume,
  onPauseTime,
  onLanguageChange,
  onProgress,
}) => {
  const videoRef = useRef(null);
  const shellRef = useRef(null);
  const draggingRef = useRef(false);
  const pendingResumeRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);

  const [speedAnchor, setSpeedAnchor] = useState(null);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [chaptersAnchor, setChaptersAnchor] = useState(null);

  // Position and play state come from the caller, which outlives this component
  // and therefore cannot lose them to a remount. Applied once the new source can
  // actually be seeked.
  useEffect(() => {
    if (resume && Number.isFinite(resume.time) && resume.time > 0) {
      pendingResumeRef.current = { ...resume };
      setCurrentTime(resume.time);
    }
    setCaptionsOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleLoadedMetadata = () => {
    const element = videoRef.current;
    if (!element) return;
    setDuration(element.duration || 0);
  };

  const applyPendingResume = () => {
    const element = videoRef.current;
    const pending = pendingResumeRef.current;
    if (!element || !pending) return;
    pendingResumeRef.current = null;

    if (pending.time > 0) {
      const limit = (element.duration || pending.time) - 0.25;
      const target = Math.max(0, Math.min(pending.time, limit));
      try {
        element.currentTime = target;
        setCurrentTime(target);
      } catch {
        /* not seekable yet — starting from 0 is the safe fallback */
      }
    }
    if (pending.playing) element.play().catch(() => {});
  };

  const handleTimeUpdate = () => {
    const element = videoRef.current;
    if (!element) return;
    if (!draggingRef.current) setCurrentTime(element.currentTime);
    if (element.buffered.length > 0) {
      setBuffered(element.buffered.end(element.buffered.length - 1));
    }
    if (onProgress) onProgress(element.currentTime, !element.paused);
  };

  const handlePause = () => {
    const element = videoRef.current;
    setPlaying(false);
    if (element && Number.isFinite(element.currentTime)) {
      if (onProgress) onProgress(element.currentTime, false);
      if (onPauseTime) onPauseTime(element.currentTime);
    }
  };

  const togglePlay = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;
    if (element.paused) element.play().catch(() => {});
    else element.pause();
  }, []);

  // Cues are parsed once per language, then filtered by the current time as
  // playback advances. Rendering them as DOM (rather than a native <track>) is
  // what allows Synthesia's larger, boxed caption style.
  const cues = useMemo(() => parseVttCues(captionsText), [captionsText]);
  const activeCues = useMemo(
    () => (captionsOn ? getActiveCues(cues, currentTime) : []),
    [captionsOn, cues, currentTime],
  );

  const handleVolume = (next) => {
    const element = videoRef.current;
    setVolume(next);
    setMuted(next === 0);
    if (element) {
      element.volume = next;
      element.muted = next === 0;
    }
  };

  const toggleMute = () => {
    const element = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (element) element.muted = next;
  };

  const handleRate = (next) => {
    const element = videoRef.current;
    setRate(next);
    if (element) element.playbackRate = next;
    setSpeedAnchor(null);
  };

  const toggleFullscreen = () => {
    const shell = shellRef.current;
    if (!shell) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else shell.requestFullscreen?.().catch(() => {});
  };

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const seekTo = (seconds) => {
    const element = videoRef.current;
    if (!element || !Number.isFinite(seconds)) return;
    const clamped = Math.min(Math.max(seconds, 0), duration || seconds);
    element.currentTime = clamped;
    setCurrentTime(clamped);
    if (onProgress) onProgress(clamped, !element.paused);
  };

  const positionFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / Math.max(1, rect.width);
    return Math.min(Math.max(ratio, 0), 1) * (duration || 0);
  };

  const handleScrubStart = (event) => {
    draggingRef.current = true;
    setCurrentTime(positionFromEvent(event));
  };

  const handleScrubMove = (event) => {
    if (!draggingRef.current) return;
    setCurrentTime(positionFromEvent(event));
  };

  const handleScrubEnd = (event) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    seekTo(positionFromEvent(event));
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const chapterMarks = useMemo(
    () =>
      (chapters || [])
        .filter(
          (chapter) =>
            Number.isFinite(chapter.timeSeconds) &&
            chapter.timeSeconds > 0 &&
            (!duration || chapter.timeSeconds < duration),
        )
        .map((chapter) => ({
          ...chapter,
          percent: duration ? (chapter.timeSeconds / duration) * 100 : 0,
        })),
    [chapters, duration],
  );

  // The scrubber is drawn as one rounded pill per chapter, separated by a small
  // gap, so the chapter break reads as a cut in the track instead of a tick laid
  // over it. The first chapter starts at zero even when Synthesia reports its
  // opening cue a second into the video.
  const trackHeight = fullscreen ? 10 : 6;
  const trackRadius = 2;

  const segments = useMemo(() => {
    if (!duration) return [];
    const boundaries = chapterMarks.length
      ? chapterMarks.map((chapter, index) => ({
          name: chapter.name,
          start: index === 0 ? 0 : chapter.timeSeconds,
          end: chapterMarks[index + 1]
            ? chapterMarks[index + 1].timeSeconds
            : duration,
        }))
      : [{ name: null, start: 0, end: duration }];

    return boundaries
      .filter((entry) => entry.end > entry.start)
      .map((entry) => {
        const span = entry.end - entry.start;
        return {
          id: `${entry.start}-${entry.end}`,
          name: entry.name,
          start: entry.start,
          grow: span,
          played: Math.min(1, Math.max(0, (currentTime - entry.start) / span)),
          loaded: Math.min(1, Math.max(0, (buffered - entry.start) / span)),
        };
      });
  }, [chapterMarks, currentTime, buffered, duration]);

  // Controls fade out while playing to keep the picture clean, and come back on
  // any pointer movement, on pause, or while a menu is open.
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);
  const menuOpen = Boolean(speedAnchor || languageAnchor || chaptersAnchor);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      const element = videoRef.current;
      if (element && !element.paused) setControlsVisible(false);
    }, 2500);
  }, []);

  useEffect(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (!playing || menuOpen) {
      setControlsVisible(true);
      return undefined;
    }
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [playing, menuOpen]);

  // Tooltips and menus portal to <body> by default, which puts them outside the
  // fullscreen element where they are invisible. Rendering them inside the
  // player shell keeps them working in fullscreen.
  const [portalEl, setPortalEl] = useState(null);
  useEffect(() => {
    setPortalEl(shellRef.current);
  }, []);

  const volumeIcon =
    muted || volume === 0 ? (
      <VolumeOffRoundedIcon fontSize="small" />
    ) : volume < 0.5 ? (
      <VolumeDownRoundedIcon fontSize="small" />
    ) : (
      <VolumeUpRoundedIcon fontSize="small" />
    );

  return (
    <Box
      ref={shellRef}
      onPointerMove={revealControls}
      onPointerDown={revealControls}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#000",
        // Lets the caption font scale to the player's own width (cqw).
        containerType: "inline-size",
        "&:fullscreen": { borderRadius: 0, aspectRatio: "auto", height: "100vh" },
      }}
    >
      <video
        ref={videoRef}
        src={src}
        title={title}
        preload="metadata"
        playsInline
        onClick={togglePlay}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={applyPendingResume}
        onLoadedData={applyPendingResume}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => {
          setPlaying(true);
          const element = videoRef.current;
          if (element && onProgress) onProgress(element.currentTime, true);
        }}
        onPause={handlePause}
        onEnded={handlePause}
        onProgress={handleTimeUpdate}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          backgroundColor: "#000",
          cursor: "pointer",
        }}
      >
      </video>

      {/*
        Synthesia-style caption overlay: large centred white text on a dark
        translucent box, sitting just above the control bar. pointer-events is
        off so clicks still reach the video, and aria-live keeps it announced.
      */}
      {activeCues.length > 0 && (
        <Box
          aria-live="polite"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: { xs: 52, sm: 64 },
            display: "flex",
            justifyContent: "center",
            px: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            dir="auto"
            sx={{
              width: "fit-content",
              maxWidth: "86%",
              mx: "auto",
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 0.75, sm: 1.25 },
              borderRadius: "6px",
              backgroundColor: "rgba(56, 57, 67, 0.64)",
              color: "#fff",
              textAlign: "center",
              lineHeight: 1.3,
              fontWeight: 500,
              // Scale with the player, not the viewport, so fullscreen and the
              // sidebar width both look right.
              fontSize: "clamp(12px, 3.4vw, 30px)",
              "@supports (container-type: inline-size)": {
                fontSize: "clamp(12px, 3.4cqw, 30px)",
              },
              textWrap: "balance",
            }}
          >
            {activeCues.map((cue) => (
              <Box component="span" key={`${cue.start}-${cue.end}`} sx={{ display: "block" }}>
                {cue.text}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: BAR_BG,
          px: fullscreen ? 2.5 : { xs: 1, sm: 1.5 },
          py: fullscreen ? 1.5 : { xs: 0.75, sm: 1 },
          backdropFilter: "blur(6px)",
          transition: "opacity 200ms ease, transform 200ms ease",
          opacity: controlsVisible ? 1 : 0,
          transform: controlsVisible ? "none" : "translateY(6px)",
          pointerEvents: controlsVisible ? "auto" : "none",
        }}
      >
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
          <IconButton
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
            sx={{
              backgroundColor: "#fff",
              color: "#18172c",
              width: 40,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f0f0f4" },
            }}
          >
            {playing ? (
              <PauseRoundedIcon fontSize="small" />
            ) : (
              <PlayArrowRoundedIcon fontSize="small" />
            )}
          </IconButton>

          <Tooltip title={muted ? "Unmute" : "Mute"} slotProps={{ popper: { container: portalEl } }}>
            <IconButton onClick={toggleMute} aria-label="Volume" sx={controlSx}>
              {volumeIcon}
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              width: 64,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              height: 34,
            }}
          >
            <Slider
              size="small"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(_event, next) => handleVolume(Array.isArray(next) ? next[0] : next)}
              aria-label="Volume level"
              sx={{
                color: "#fff",
                py: 0,
                height: 10,
                "& .MuiSlider-rail": {
                  height: 4,
                  borderRadius: 2,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#fff",
                  opacity: 0.32,
                },
                "& .MuiSlider-track": {
                  height: 4,
                  borderRadius: 2,
                  border: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                },
                "& .MuiSlider-thumb": { width: 10, height: 10 },
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#fff",
              fontSize: "0.75rem",
              fontVariantNumeric: "tabular-nums",
              minWidth: 40,
              display: { xs: "none", sm: "block" },
            }}
          >
            {formatPlayerTime(currentTime)}
          </Typography>

          <Box
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(currentTime)}
            tabIndex={0}
            onPointerDown={handleScrubStart}
            onPointerMove={handleScrubMove}
            onPointerUp={handleScrubEnd}
            onPointerLeave={handleScrubEnd}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") seekTo(currentTime + 5);
              if (event.key === "ArrowLeft") seekTo(currentTime - 5);
            }}
            sx={{
              position: "relative",
              flex: 1,
              height: fullscreen ? 30 : 22,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              touchAction: "none",
              minWidth: 60,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: trackHeight,
                display: "flex",
                gap: "4px",
              }}
            >
              {segments.map((segment) => (
                <Tooltip
                  key={segment.id}
                  title={
                    segment.name
                      ? `${segment.name} · ${formatPlayerTime(segment.start)}`
                      : ""
                  }
                  placement="top"
                  slotProps={{ popper: { container: portalEl } }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      flex: `${segment.grow} 1 0`,
                      height: "100%",
                      minWidth: 2,
                      // The track is only a few pixels tall, so the hover target
                      // is widened without changing the track's layout.
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: "-12px -2px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: `${trackRadius}px`,
                        bgcolor: "rgba(255,255,255,0.24)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: `${segment.loaded * 100}%`,
                          bgcolor: "rgba(255,255,255,0.4)",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: `${segment.played * 100}%`,
                          bgcolor: "#fff",
                        }}
                      />
                    </Box>
                  </Box>
                </Tooltip>
              ))}

              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: `${progressPercent}%`,
                  width: fullscreen ? 14 : 11,
                  height: fullscreen ? 14 : 11,
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
            </Box>
          </Box>

          <Typography
            sx={{
              color: "#fff",
              fontSize: "0.75rem",
              fontVariantNumeric: "tabular-nums",
              minWidth: 40,
            }}
          >
            {formatPlayerTime(duration)}
          </Typography>

          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ ml: 0.5 }}>
            {languages.length > 1 && (
              <>
                <Tooltip title="Language" slotProps={{ popper: { container: portalEl } }}>
                  <IconButton
                    onClick={(event) => setLanguageAnchor(event.currentTarget)}
                    aria-label="Change language"
                    sx={{ ...pillSx, backgroundColor: "rgba(255,255,255,0.18)" }}
                  >
                    {languageCode}
                  </IconButton>
                </Tooltip>
                <Menu
                  container={portalEl}
                  anchorEl={languageAnchor}
                  open={Boolean(languageAnchor)}
                  onClose={() => setLanguageAnchor(null)}
                  slotProps={{ paper: { sx: { borderRadius: "10px", minWidth: 180 } } }}
                >
                  {languages.map((language) => (
                    <MenuItem
                      key={language.code}
                      selected={language.code === languageCode}
                      onClick={() => {
                        setLanguageAnchor(null);
                        if (onLanguageChange) onLanguageChange(language.code);
                      }}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      <Box sx={{ flex: 1 }}>{language.label}</Box>
                      {language.code === languageCode && (
                        <CheckRoundedIcon fontSize="small" sx={{ ml: 2 }} />
                      )}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            <Tooltip title={cues.length > 0 ? "Captions" : "No captions for this language"} slotProps={{ popper: { container: portalEl } }}>
              <span>
                <IconButton
                  onClick={() => setCaptionsOn((value) => !value)}
                  disabled={cues.length === 0}
                  aria-label="Captions"
                  aria-pressed={captionsOn}
                  sx={captionsOn ? pillActiveSx : pillSx}
                >
                  <ClosedCaptionRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Playback speed" slotProps={{ popper: { container: portalEl } }}>
              <IconButton
                onClick={(event) => setSpeedAnchor(event.currentTarget)}
                aria-label="Playback speed"
                sx={rate !== 1 ? pillActiveSx : pillSx}
              >
                {rate}x
              </IconButton>
            </Tooltip>
            <Menu
              container={portalEl}
              anchorEl={speedAnchor}
              open={Boolean(speedAnchor)}
              onClose={() => setSpeedAnchor(null)}
              slotProps={{ paper: { sx: { borderRadius: "10px", minWidth: 120 } } }}
            >
              {SPEEDS.map((speed) => (
                <MenuItem
                  key={speed}
                  selected={speed === rate}
                  onClick={() => handleRate(speed)}
                  sx={{ fontSize: "0.875rem" }}
                >
                  <Box sx={{ flex: 1 }}>{speed}x</Box>
                  {speed === rate && <CheckRoundedIcon fontSize="small" sx={{ ml: 2 }} />}
                </MenuItem>
              ))}
            </Menu>

            {chapterMarks.length > 0 && (
              <>
                <Tooltip title="Chapters" slotProps={{ popper: { container: portalEl } }}>
                  <IconButton
                    onClick={(event) => setChaptersAnchor(event.currentTarget)}
                    aria-label="Chapters"
                    sx={pillSx}
                  >
                    <SubtitlesRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  container={portalEl}
                  anchorEl={chaptersAnchor}
                  open={Boolean(chaptersAnchor)}
                  onClose={() => setChaptersAnchor(null)}
                  slotProps={{
                    paper: { sx: { borderRadius: "10px", minWidth: 240, maxHeight: 320 } },
                  }}
                >
                  {chapters.map((chapter) => (
                    <MenuItem
                      key={`${chapter.name}-${chapter.timeSeconds}`}
                      onClick={() => {
                        setChaptersAnchor(null);
                        seekTo(chapter.timeSeconds);
                      }}
                      sx={{ fontSize: "0.85rem", gap: 1.5 }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.72rem",
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 44,
                        }}
                      >
                        {formatPlayerTime(chapter.timeSeconds)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>{chapter.name}</Box>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            <Tooltip title={fullscreen ? "Exit full screen" : "Full screen"} slotProps={{ popper: { container: portalEl } }}>
              <IconButton
                onClick={toggleFullscreen}
                aria-label="Full screen"
                sx={controlSx}
              >
                {fullscreen ? (
                  <FullscreenExitRoundedIcon fontSize="small" />
                ) : (
                  <FullscreenRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
